import { useQuery, useReactiveVar } from '@apollo/client';
import { Checkbox, Stack } from '@fluentui/react';
import {
  DropdownFilter,
  FilterArrayType,
  FilterElement,
} from 'common/components/Filters';
import { getUpdatedFilterArray } from 'common/components/Filters/utils';
import {
  ColumnData,
  SearchBar,
  SearchBarProps,
} from 'common/components/SearchBar';
import { formatDropdownOptions } from 'common/utils';
import { checkBoxStyle } from 'common/utils/listViewCheckBoxStyles';
import React, { useState } from 'react';
import { globalMode } from 'utility/cache/ui';
import { PaymentTrackingTypes } from './__generated__/PaymentTrackingTypes';
import { useStyles } from './index.styles';

import PAYMENT_TYPES_QUERY from './PaymentTrackingTypes.graphql';

interface PaymentSearchBarProps extends SearchBarProps, MenuActionBarProps {
  onHintsViewToggle?: () => void;
  columns: ColumnData[];
  onFilterChange: (filterArray: FilterArrayType[]) => void;
  onToggleVisibility?: (columns: ColumnData[]) => void;
  onHistoryCheckbox?: () => void;
}

export const PaymentTrackingSearchBar: React.FC<PaymentSearchBarProps> = ({
  columns,
  onFilterChange,
  onEnterPress,
  hintsAvailable,
  onRefresh,
  searchInputPlaceholderText,
  onToggleVisibility,
  onHintsViewToggle,
  onHistoryToggle: onHistoryToggle,
  urlParams,
  onGlobalToggle,
}) => {
  const [filterTypes, setFilterTypes] = useState<FilterArrayType[]>([]);
  const { data: paymentTypesData } =
    useQuery<PaymentTrackingTypes>(PAYMENT_TYPES_QUERY);
  const typeOptions = formatDropdownOptions(
    paymentTypesData?.paymentTransactionTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.transactionType!,
      includeAll: true,
    }
  );
  const businessUnitOptions = formatDropdownOptions(
    paymentTypesData?.companyBusinessUnits?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );
  const currencyOptions = formatDropdownOptions(
    paymentTypesData?.companyCurrencies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );

  const companiesOptions = formatDropdownOptions(
    paymentTypesData?.companies?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.name!,
      includeAll: true,
    }
  );

  const statusOptions = formatDropdownOptions(
    paymentTypesData?.invoiceStatusTypes?.nodes,
    {
      getKey: (item) => item.id,
      getText: (item) => item.statusType!,
      includeAll: true,
    }
  );

  const onOptionChange = (filterdata: FilterArrayType) => {
    const availableFilters = [...filterTypes];
    const newFilters = getUpdatedFilterArray(filterdata, availableFilters);
    onFilterChange?.(newFilters);
    setFilterTypes(newFilters);
  };

  const filters: FilterElement[] = [
    <DropdownFilter
      filterKey={'transactionTypeId'}
      options={typeOptions || []}
      placeholder={'Types'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'businessUnitId'}
      options={businessUnitOptions || []}
      placeholder={'Business Unit'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'currencyId'}
      options={currencyOptions || []}
      placeholder={'Currency'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey={'_companyId'}
      options={companiesOptions || []}
      placeholder={'Productions'}
      onOptionChange={onOptionChange}
    />,
    <DropdownFilter
      filterKey="_statusTypeId"
      options={statusOptions}
      placeholder="Status"
      onOptionChange={onOptionChange}
    />,
  ];

  return (
    <SearchBar
      urlParams={urlParams}
      onFilterClear={() => {
        if (filterTypes.length) onFilterChange?.([]);
        setFilterTypes([]);
      }}
      columns={columns}
      searchInputPlaceholderText={searchInputPlaceholderText}
      onEnterPress={onEnterPress}
      onRefresh={onRefresh}
      onGlobalToggle={onGlobalToggle}
      hintsAvailable={hintsAvailable}
      onHintsViewToggle={onHintsViewToggle}
      onToggleVisibility={onToggleVisibility}
      renderActions={<MenuActionBar onHistoryToggle={onHistoryToggle} />}
      filters={filters}
    />
  );
};
interface MenuActionBarProps {
  onHistoryToggle?: (value: boolean) => void;
}
export const MenuActionBar: React.FC<MenuActionBarProps> = ({
  onHistoryToggle: onHistoryToggle,
}) => {
  const styles = useStyles();
  const globalState = useReactiveVar(globalMode);

  return (
    <Stack
      className={styles.actionsWrapperStyle}
      horizontalAlign="center"
      verticalAlign="center"
      horizontal
      tokens={{ childrenGap: 25 }}
    >
      <Checkbox
        disabled={globalState}
        styles={checkBoxStyle}
        label="History"
        onChange={(_, checked) => onHistoryToggle?.(checked!)}
      />
    </Stack>
  );
};
